import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    FormGroup,
    Label,
    Input,
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Spinner,
    Button,
} from "reactstrap";
import { MapPin } from "react-feather";

import {
    GoogleMap,
    Marker,
    LoadScript,
    Autocomplete,
} from "@react-google-maps/api";

const defaultCenter = { lat: 33.6844, lng: 73.0479 }; // Islamabad

const LocationPicker = ({ location, onLocationChange, disabled = false }) => {
    const [currentLoc, setCurrentLoc] = useState({
        latitude: location?.latitude || "",
        longitude: location?.longitude || "",
        address: location?.address || "",
    });

    const [addressLoading, setAddressLoading] = useState(false);
    const googleAutocompleteRef = useRef(null);
    const mapRef = useRef(null);

    // Sync external location changes
    useEffect(() => {
        setCurrentLoc({
            latitude: location?.latitude || "",
            longitude: location?.longitude || "",
            address: location?.address || "",
        });
    }, [location?.latitude, location?.longitude, location?.address]);

    // When user selects place from autocomplete
    const onPlaceChanged = () => {
        const place = googleAutocompleteRef.current?.getPlace();
        if (!place || !place.geometry) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address || place.name || "";

        const newLoc = {
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
            address,
        };
        setCurrentLoc(newLoc);
        onLocationChange?.(newLoc);

        if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(15);
        }
    };

    // When user drags the marker on the map
    const onMarkerDragEnd = async (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        setAddressLoading(true);

        if (!window.google) return;

        // Reverse geocode lat/lng to address using Google Maps Geocoder
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            let address = "";
            if (status === "OK" && results[0]) {
                address = results[0].formatted_address;
            }
            const newLoc = {
                latitude: lat.toFixed(6),
                longitude: lng.toFixed(6),
                address,
            };
            setCurrentLoc(newLoc);
            onLocationChange?.(newLoc);
            setAddressLoading(false);
        });
    };

    // Use browser geolocation to get current location
    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser.");
            return;
        }

        setAddressLoading(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;

                const newLoc = {
                    latitude: lat.toFixed(6),
                    longitude: lng.toFixed(6),
                    address: "Loading address...",
                };
                setCurrentLoc(newLoc);

                // Reverse geocode to get address
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    let address = "";
                    if (status === "OK" && results[0]) {
                        address = results[0].formatted_address;
                    }
                    const finalLoc = {
                        latitude: lat.toFixed(6),
                        longitude: lng.toFixed(6),
                        address,
                    };
                    setCurrentLoc(finalLoc);
                    onLocationChange?.(finalLoc);
                    setAddressLoading(false);
                });
            },
            (error) => {
                alert("Failed to get current location.");
                setAddressLoading(false);
            }
        );
    };

    // Manual address input change handler
    const handleAddressChange = (e) => {
        const newAddress = e.target.value;
        setCurrentLoc((prev) => ({ ...prev, address: newAddress }));
        onLocationChange?.({
            latitude: currentLoc.latitude,
            longitude: currentLoc.longitude,
            address: newAddress,
        });
    };

    return (
        <div className="location-picker">
            <Row className="mt-3" style={{ display: "none" }}>
                <Col md={4}>
                    <FormGroup>
                        <Label>Address</Label>
                        <Input
                            type="text"
                            value={addressLoading ? "Loading address..." : currentLoc.address}
                            disabled={disabled || addressLoading}
                            onChange={handleAddressChange}
                            placeholder="Search or drag marker to update"
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Latitude</Label>
                        <Input type="text" value={currentLoc.latitude} readOnly disabled />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Longitude</Label>
                        <Input type="text" value={currentLoc.longitude} readOnly disabled />
                    </FormGroup>
                </Col>
            </Row>
            <Card style={{ boxShadow: "none" }}>
                <CardHeader className="d-flex align-items-center gap-2">
                    <MapPin size={16} className="text-primary" />
                    <span className="fw-medium">Pick Location on Map</span>
                </CardHeader>
                <CardBody>

                    <Autocomplete
                        onLoad={(autocomplete) => (googleAutocompleteRef.current = autocomplete)}
                        onPlaceChanged={onPlaceChanged}
                    >
                        <Input
                            type="text"
                            value={currentLoc.address}
                            disabled={disabled}
                            placeholder="Search address..."
                            onChange={handleAddressChange}
                            className="mb-2"
                        />
                    </Autocomplete>

                    <GoogleMap
                        mapContainerStyle={{ height: "400px", width: "100%" }}
                        center={
                            currentLoc.latitude && currentLoc.longitude
                                ? {
                                    lat: parseFloat(currentLoc.latitude),
                                    lng: parseFloat(currentLoc.longitude),
                                }
                                : defaultCenter
                        }
                        zoom={14}
                        onLoad={(map) => (mapRef.current = map)}
                        options={{ streetViewControl: false }}
                    >
                        <Marker
                            position={
                                currentLoc.latitude && currentLoc.longitude
                                    ? {
                                        lat: parseFloat(currentLoc.latitude),
                                        lng: parseFloat(currentLoc.longitude),
                                    }
                                    : defaultCenter
                            }
                            draggable={!disabled}
                            onDragEnd={onMarkerDragEnd}
                        />
                    </GoogleMap>


                    <Button
                        className="mt-2"
                        color="outline-primary"
                        onClick={handleUseCurrentLocation}
                        disabled={disabled || addressLoading}
                    >
                        📍 Use Current Location
                    </Button>
                </CardBody>
            </Card>
        </div>
    );
};

export default LocationPicker;
